import React, {FC, useState} from 'react';
import {Card, InputNumber, Switch, Button, Space, Typography, message} from 'antd';
import styled from 'styled-components';
import {CurrencyEntity} from '../../../../proto/generated/api_entities_pb';
import {BillingApi} from '../../../../API/ApiClasses/BillingApi';
import {BillingApiCurrencyUpdateRequest} from '../../../../proto/generated/api_entities_pb';
import {useIntl} from 'react-intl';
import {defineMessages} from 'react-intl';

const {Text} = Typography;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const CardLabel = styled(Text)`
  font-weight: 500;
  min-width: 120px;
`;

const CardValue = styled.div`
  flex: 1;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const CurrencyCardMessages = defineMessages({
  name: {
    id: "currencyCard.name",
    defaultMessage: "Name",
  },
  rate: {
    id: "currencyCard.rate",
    defaultMessage: "Rate",
  },
  isBase: {
    id: "currencyCard.isBase",
    defaultMessage: "Base currency",
  },
  update: {
    id: "currencyCard.update",
    defaultMessage: "Update rate",
  },
  setBase: {
    id: "currencyCard.setBase",
    defaultMessage: "Set as base",
  },
  cancel: {
    id: "currencyCard.cancel",
    defaultMessage: "Cancel",
  },
  updateSuccess: {
    id: "currencyCard.updateSuccess",
    defaultMessage: "Currency rate updated successfully",
  },
  setBaseSuccess: {
    id: "currencyCard.setBaseSuccess",
    defaultMessage: "Base currency changed successfully",
  },
  error: {
    id: "currencyCard.error",
    defaultMessage: "Failed to update currency",
  },
  baseCurrencyNote: {
    id: "currencyCard.baseCurrencyNote",
    defaultMessage: "Base currency rate is always 1.0",
  },
});

interface ICurrencyCard {
  currency: CurrencyEntity;
  onUpdate: () => void;
  onSetBase: () => void;
}

export const CurrencyCard: FC<ICurrencyCard> = ({currency, onUpdate, onSetBase}) => {
  const intl = useIntl();
  const [rate, setRate] = useState<number | null>(null);
  const [isBase, setIsBase] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const currencyRate = currency.getRate();
  const currencyIsBase = currency.getIsbase();
  
  // Initialize state with currency values
  React.useEffect(() => {
    if (currencyRate !== null && currencyRate !== undefined && rate === null) {
      setRate(currencyRate);
    }
    setIsBase(currencyIsBase);
  }, [currencyRate, currencyIsBase]);

  const handleUpdate = async () => {
    if (rate === null || rate <= 0) {
      message.error('Rate must be greater than 0');
      return;
    }

    try {
      setLoading(true);
      const req = new BillingApiCurrencyUpdateRequest()
        .setCurrencyid(currency.getCurrencyid())
        .setRate(rate);

      const response = await BillingApi.updateCurrency(req);
      
      if (response.getResultresponse()?.getIssuccessful()) {
        message.success(intl.formatMessage(CurrencyCardMessages.updateSuccess));
        setIsEditing(false);
        onUpdate();
      } else {
        const errorMsg = response.getResultresponse()?.getErrormessage() || intl.formatMessage(CurrencyCardMessages.error);
        message.error(errorMsg);
      }
    } catch (error) {
      message.error(intl.formatMessage(CurrencyCardMessages.error));
      console.error('Failed to update currency:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetBase = async () => {
    try {
      setLoading(true);
      const req = new BillingApiCurrencyUpdateRequest()
        .setCurrencyid(currency.getCurrencyid())
        .setIsbase(true);

      const response = await BillingApi.updateCurrency(req);
      
      if (response.getResultresponse()?.getIssuccessful()) {
        message.success(intl.formatMessage(CurrencyCardMessages.setBaseSuccess));
        onSetBase();
      } else {
        const errorMsg = response.getResultresponse()?.getErrormessage() || intl.formatMessage(CurrencyCardMessages.error);
        message.error(errorMsg);
      }
    } catch (error) {
      message.error(intl.formatMessage(CurrencyCardMessages.error));
      console.error('Failed to set base currency:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setRate(null);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setRate(currencyRate);
    setIsEditing(true);
  };

  return (
    <Card>
      <CardContent>
        <CardRow>
          <CardLabel>{intl.formatMessage(CurrencyCardMessages.name)}:</CardLabel>
          <CardValue>
            <Text strong>{currency.getName()}</Text>
          </CardValue>
        </CardRow>

        <CardRow>
          <CardLabel>{intl.formatMessage(CurrencyCardMessages.rate)}:</CardLabel>
          <CardValue>
            {isEditing ? (
              <InputNumber
                value={rate ?? currencyRate}
                onChange={(value) => setRate(value)}
                min={0.0001}
                step={0.01}
                precision={4}
                style={{ width: '100%' }}
                disabled={currencyIsBase}
              />
            ) : (
              <Text>{currencyRate ?? 'N/A'}</Text>
            )}
            {currencyIsBase && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({intl.formatMessage(CurrencyCardMessages.baseCurrencyNote)})
              </Text>
            )}
          </CardValue>
        </CardRow>

        <CardRow>
          <CardLabel>{intl.formatMessage(CurrencyCardMessages.isBase)}:</CardLabel>
          <CardValue>
            <Switch checked={currencyIsBase} disabled />
          </CardValue>
        </CardRow>

        <ActionsContainer>
          {!currencyIsBase && (
            <>
              {isEditing ? (
                <Space>
                  <Button onClick={handleCancel} disabled={loading}>
                    {intl.formatMessage(CurrencyCardMessages.cancel)}
                  </Button>
                  <Button type="primary" onClick={handleUpdate} loading={loading}>
                    {intl.formatMessage(CurrencyCardMessages.update)}
                  </Button>
                </Space>
              ) : (
                <Button onClick={handleEdit}>
                  {intl.formatMessage(CurrencyCardMessages.update)}
                </Button>
              )}
              <Button type="primary" onClick={handleSetBase} loading={loading}>
                {intl.formatMessage(CurrencyCardMessages.setBase)}
              </Button>
            </>
          )}
        </ActionsContainer>
      </CardContent>
    </Card>
  );
};

